import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Divider, Stack, Tooltip } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

import {
  partnerAccountQuery,
  partnerAccountTransactionsQuery,
  type IPartnerAccountTransactionDto,
} from '@/entities/partner';
import { useAppQuery } from '@/shared/lib/useAppQuery';
import { Paper } from '@/shared/ui';
import { Button } from '@/shared/ui/lib/Button';
import { Loader } from '@/shared/ui/Loader';
import { CustomTypography } from '@/shared/ui/Typography';
import { dayjs } from '@/shared/utils/dayjs';

import { formatAffiliateAmount } from '../formatAmount';
import { TopUpDialog } from '../dialogs/TopUpDialog';

const formatTxDate = (value?: string | null) => {
  if (!value) return '—';
  const d = dayjs(value);
  return d.isValid() ? d.format('DD.MM.YYYY HH:mm') : '—';
};

const txTypeLabel = (
  type: IPartnerAccountTransactionDto['type'],
  comment: string | null,
  t: (key: string) => string,
): string => {
  const i18nKey = `common:affiliatePage.account.txTypes.${type}`;
  const translated = t(i18nKey);
  if (translated && translated !== i18nKey) {
    return comment ? `${translated} — ${comment}` : translated;
  }
  // Fallback to raw type or comment
  return comment ?? type;
};

/**
 * Partner account tab. Shows balance KPIs and a transaction ledger.
 */
export function AccountTab(): ReactNode {
  const { t } = useTranslation();
  const [topUpOpen, setTopUpOpen] = useState(false);

  const { data: account, isError: accountIsError, error: accountError, isPending } = useAppQuery({ ...partnerAccountQuery() });
  const { data: txs, isError: txsIsError, error: txsError } = useAppQuery({
    ...partnerAccountTransactionsQuery({ limit: 50, offset: 0 }),
  });

  const balanceMajor = (account?.balanceMinor ?? 0) / 100;
  const currency = account?.currency ?? 'RUB';
  const partnerDiscountRate = account?.rates.partnerDiscountRate ?? 0;
  const partnerDiscountNextReviewAt = account?.rates.partnerDiscountNextReviewAt;
  const referralCommissionRate = account?.rates.commissionRate ?? 25;
  const transactions = txs?.items ?? [];

  if (isPending && !account) {
    return <Loader containerSize="medium" />;
  }

  if (accountIsError) {
    return <Alert severity="error">Не удалось загрузить данные счёта</Alert>;
  }

  return (
    <Stack spacing={2}>
      <Paper sx={{ p: 3, borderRadius: '20px' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' },
            gap: 3,
            alignItems: 'center',
          }}
        >
          <Stack spacing={0.5}>
            <CustomTypography variant="caption" color="text.secondary">
              {t('common:affiliatePage.account.balance')}
            </CustomTypography>
            <CustomTypography variant="h5" sx={{ fontWeight: 700 }}>
              {formatAffiliateAmount(balanceMajor, currency)}
            </CustomTypography>
          </Stack>
          <Stack spacing={0.5}>
            <CustomTypography variant="caption" color="text.secondary">
              {t('common:affiliatePage.account.partnerDiscount')}
            </CustomTypography>
            <CustomTypography variant="h5" sx={{ fontWeight: 700 }}>
              {partnerDiscountRate}%
            </CustomTypography>
            {partnerDiscountNextReviewAt && (
              <CustomTypography variant="caption" color="text.secondary">
                {t('common:affiliatePage.account.partnerDiscountNextReview', {
                  date: formatTxDate(partnerDiscountNextReviewAt),
                })}
              </CustomTypography>
            )}
          </Stack>
          <Stack spacing={0.5}>
            <CustomTypography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {t('common:affiliatePage.account.referralCommission')}
              <Tooltip title={t('common:affiliatePage.account.referralCommissionHint') ?? ''} arrow>
                <HelpOutlineIcon fontSize="inherit" sx={{ opacity: 0.6 }} />
              </Tooltip>
            </CustomTypography>
            <CustomTypography variant="h5" sx={{ fontWeight: 700 }}>
              {referralCommissionRate}%
            </CustomTypography>
          </Stack>
          <Box sx={{ display: 'flex', justifyContent: { xs: 'stretch', md: 'flex-end' } }}>
            <Button
              variant="contained"
              onClick={() => setTopUpOpen(true)}
              sx={{ width: { xs: '100%', md: 'auto' } }}
            >
              {t('common:affiliatePage.account.topUp')}
            </Button>
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ p: 0, borderRadius: '20px', overflow: 'hidden' }}>
        <Box sx={{ p: 3 }}>
          <CustomTypography variant="h6" sx={{ fontWeight: 700 }}>
            {t('common:affiliatePage.account.transactions')}
          </CustomTypography>
        </Box>
        <Divider />
        <Box sx={{ display: 'grid', gridTemplateColumns: '180px 140px 140px 1fr', px: 3, py: 1.5, opacity: 0.6 }}>
          <CustomTypography variant="caption">{t('common:affiliatePage.account.tx.date')}</CustomTypography>
          <CustomTypography variant="caption">{t('common:affiliatePage.account.tx.amount')}</CustomTypography>
          <CustomTypography variant="caption">{t('common:affiliatePage.account.tx.balanceAfter')}</CustomTypography>
          <CustomTypography variant="caption">{t('common:affiliatePage.account.tx.comment')}</CustomTypography>
        </Box>
        <Divider />
        {txsIsError ? (
          <Alert severity="error">Не удалось загрузить историю операций</Alert>
        ) : transactions.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
            <CustomTypography>
              {t('common:affiliatePage.account.tx.empty')}
            </CustomTypography>
          </Box>
        ) : (
          transactions.map((tx) => {
            const amountMajor = tx.amountMinor / 100;
            const balanceAfterMajor = tx.balanceAfterMinor / 100;
            return (
              <Box
                key={tx.id}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '180px 140px 140px 1fr',
                  px: 3,
                  py: 1.5,
                  borderBottom: '1px solid',
                  borderColor: 'custom.black.10',
                }}
              >
                <CustomTypography>{formatTxDate(tx.occurredAt)}</CustomTypography>
                <CustomTypography
                  sx={{
                    color:
                      amountMajor < 0
                        ? 'error.main'
                        : amountMajor > 0
                        ? 'actions.go'
                        : 'text.primary',
                  }}
                >
                  {amountMajor > 0 ? '+' : ''}
                  {formatAffiliateAmount(amountMajor, tx.currency)}
                </CustomTypography>
                <CustomTypography>
                  {formatAffiliateAmount(balanceAfterMajor, tx.currency)}
                </CustomTypography>
                <CustomTypography>{txTypeLabel(tx.type, tx.comment, t)}</CustomTypography>
              </Box>
            );
          })
        )}
      </Paper>

      <TopUpDialog open={topUpOpen} onClose={() => setTopUpOpen(false)} />
    </Stack>
  );
}
